"use client";

import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import { Link } from "@/i18n/navigation";
import { BlogPreview, BlogWidget } from "@/types/home";

interface BlogSectionProps {
      data?: BlogWidget;
      blogs: BlogPreview[];
}

const FALLBACK_IMAGE =
      "https://images.unsplash.com/photo-1629909613654-28e377c37b09?q=80&w=1200&auto=format&fit=crop";

function BlogCard({ blog }: { blog: BlogPreview }) {
      const displayImage = blog.coverImageUrl || FALLBACK_IMAGE;

      return (
            <article className="group flex flex-col h-full bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.02)] hover:shadow-[0_16px_32px_rgb(0,0,0,0.06)] transition-all duration-500 hover:-translate-y-1 overflow-hidden">
                  <Link
                        href={`/blog/${blog.slug}` as any}
                        className="block relative aspect-[16/10] w-full overflow-hidden"
                  >
                        <Image
                              src={displayImage}
                              alt={blog.coverImageAlt || blog.title}
                              fill
                              className="object-cover transition-transform duration-700 group-hover:scale-110"
                              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                              unoptimized={!blog.coverImageUrl}
                        />
                        <div className="absolute inset-0 bg-[#404041]/5 group-hover:bg-[#404041]/15 transition-colors duration-500" />
                  </Link>

                  <div className="flex flex-col grow p-6">
                        <Link href={`/blog/${blog.slug}` as any}>
                              <h3 className="text-lg font-bold text-[#404041] leading-snug group-hover:text-[#A68F72] transition-colors line-clamp-2 mb-3">
                                    {blog.title}
                              </h3>
                        </Link>

                        {blog.shortDescription && (
                              <p className="text-gray-500 font-light text-sm line-clamp-3 mb-5 leading-relaxed grow">
                                    {blog.shortDescription}
                              </p>
                        )}

                        <Link
                              href={`/blog/${blog.slug}` as any}
                              className="mt-auto inline-flex items-center gap-1.5 text-xs font-bold text-[#404041] group-hover:text-[#A68F72] transition-colors"
                        >
                              Devamını Oku
                              <ArrowRight
                                    size={14}
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                              />
                        </Link>
                  </div>
            </article>
      );
}

export default function BlogSection({ data, blogs }: BlogSectionProps) {
      if (!blogs || blogs.length === 0) return null;

      const badge = data?.badge;
      const title = data?.title;
      const description = data?.description;
      const viewAllText = data?.viewAllText;

      return (
            <section className="relative py-20 md:py-24 bg-[#faf9f6] overflow-hidden">
                  <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#A68F72]/[0.06] rounded-full blur-3xl translate-x-1/3 -translate-y-1/3 pointer-events-none" />
                  <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#A68F72]/[0.04] rounded-full blur-3xl -translate-x-1/4 translate-y-1/4 pointer-events-none" />

                  <div className="container mx-auto px-4 md:px-8 relative z-10">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                              <div className="max-w-2xl">
                                    {badge && (
                                          <span className="inline-block py-1.5 px-4 rounded-full bg-[#A68F72]/10 border border-[#A68F72]/20 text-[#A68F72] text-xs font-semibold tracking-widest uppercase mb-4">
                                                {badge}
                                          </span>
                                    )}
                                    {title && (
                                          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#404041] mb-4 tracking-tight leading-[1.1]">
                                                {title}
                                          </h2>
                                    )}
                                    {description && (
                                          <p className="text-base md:text-lg text-gray-600 font-light leading-relaxed">
                                                {description}
                                          </p>
                                    )}
                              </div>

                              {viewAllText && (
                                    <Link
                                          href="/blog"
                                          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-[#A68F72]/30 bg-white text-[#A68F72] text-sm font-semibold hover:bg-[#A68F72] hover:text-white hover:border-[#A68F72] transition-all duration-300 group whitespace-nowrap shadow-sm hover:shadow-md"
                                    >
                                          {viewAllText}
                                          <ArrowRight
                                                size={16}
                                                className="transition-transform duration-300 group-hover:translate-x-1"
                                          />
                                    </Link>
                              )}
                        </div>

                        {/* Mobile carousel */}
                        <div className="md:hidden -mx-4">
                              <Swiper
                                    modules={[FreeMode, Pagination]}
                                    spaceBetween={16}
                                    slidesPerView={1.1}
                                    freeMode={true}
                                    pagination={{
                                          clickable: true,
                                          bulletClass: "blog-bullet",
                                          bulletActiveClass: "blog-bullet-active",
                                    }}
                                    className="px-4 pb-12 overflow-visible!"
                              >
                                    {blogs.map((blog) => (
                                          <SwiperSlide key={blog.id} className="h-auto">
                                                <BlogCard blog={blog} />
                                          </SwiperSlide>
                                    ))}
                              </Swiper>
                        </div>

                        {/* Desktop grid */}
                        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                              {blogs.map((blog) => (
                                    <BlogCard key={blog.id} blog={blog} />
                              ))}
                        </div>
                  </div>

                  <style jsx global>{`
                        .blog-bullet {
                              display: inline-block;
                              width: 24px;
                              height: 3px;
                              border-radius: 9999px;
                              margin: 0 3px;
                              background: rgba(166, 143, 114, 0.25);
                              transition: background 0.3s, width 0.3s;
                              cursor: pointer;
                        }
                        .blog-bullet-active {
                              background: #a68f72;
                              width: 36px;
                        }
                  `}</style>
            </section>
      );
}
